/**
 * ParentDashboard — landing for users with role="parent".
 * Shows: family credit balance, list of children (with status), recent
 * usage journal, action to add another child.
 *
 * Route: /parent
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Heart, Coins, Plus, X as XIcon, Loader2, RefreshCcw,
  CheckCircle2, Clock, XCircle, Activity,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { GlassCard, SectionHeader, PageLoading } from '../components/shared';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const headers = () => {
  const t = localStorage.getItem('noor_albayan_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STATUS_META = {
  pending:   { fr: 'En attente',  cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30',  icon: Clock },
  confirmed: { fr: 'Confirmé',    cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', icon: CheckCircle2 },
  declined:  { fr: 'Refusé',      cls: 'bg-rose-500/15 text-rose-300 border-rose-500/30', icon: XCircle },
};

export const ParentDashboardPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [children, setChildren] = useState([]);
  const [account, setAccount] = useState(null);
  const [usage, setUsage] = useState([]);
  const [coparents, setCoparents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  const refresh = async () => {
    setLoading(true); setError('');
    try {
      const [c, a, u, cp] = await Promise.all([
        axios.get(`${API_URL}/api/parent/children`, { headers: headers() }),
        axios.get(`${API_URL}/api/credits/me`, { headers: headers() }),
        axios.get(`${API_URL}/api/credits/usage?limit=20`, { headers: headers() }),
        axios.get(`${API_URL}/api/parent/coparents`, { headers: headers() }),
      ]);
      setChildren(c.data || []);
      setAccount(a.data?.account || null);
      setUsage(u.data || []);
      setCoparents(cp.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur de chargement.");
    } finally { setLoading(false); }
  };

  const submitNewChild = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    setAdding(true); setError('');
    try {
      await axios.post(`${API_URL}/api/parent/children`,
        { children_emails: [newEmail.trim()] },
        { headers: headers() });
      setNewEmail('');
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || "Erreur lors de l'ajout.");
    } finally { setAdding(false); }
  };

  const removeChild = async (sid) => {
    if (!window.confirm('Retirer ce lien parental ? Cette action est réversible (vous pouvez le relier).')) return;
    try {
      await axios.delete(`${API_URL}/api/parent/children/${sid}`,
        { headers: headers() });
      await refresh();
    } catch (e) { setError(e?.response?.data?.detail || "Erreur."); }
  };

  if (authLoading || loading) return <PageLoading />;

  const remaining = account?.remaining_credits ?? 0;
  const total = account?.total_credits ?? 0;
  const used = account?.used_credits ?? 0;
  const lowBalance = total > 0 && remaining < Math.max(10, total * 0.1);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12 lg:pt-28" data-testid="parent-dashboard">
      <SectionHeader overline="Espace parent"
                     title="Mes enfants"
                     subtitle="Suivez la progression et le solde familial." />

      {error && (
        <div className="my-4 p-3 rounded-lg bg-danger/15 border border-danger/30 text-danger text-sm" data-testid="parent-error">
          {error}
        </div>
      )}

      {/* CREDIT BALANCE */}
      <GlassCard className={`mt-6 p-5 ${lowBalance ? 'border-rose-500/40' : ''}`} data-testid="parent-credits-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-primary/15 flex items-center justify-center">
              <Coins className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-text-muted">Solde familial</p>
              <p className="text-3xl font-serif text-text-primary">
                {remaining}
                <span className="text-sm text-text-muted ml-2">/ {total} crédits</span>
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" disabled
                  title="Disponible prochainement"
                  data-testid="parent-topup-btn">
            <Coins className="w-4 h-4 mr-1" /> Recharger
          </Button>
        </div>
        {/* Progress bar */}
        <div className="mt-4 h-2 rounded-full bg-surface overflow-hidden">
          <div className="h-full bg-primary"
               style={{ width: `${total > 0 ? Math.min(100, (used / total) * 100) : 0}%` }} />
        </div>
        {lowBalance && (
          <p className="mt-3 text-xs text-rose-300">
            Solde bas — la mémorisation et l'analyse audio risquent d'être bloquées bientôt.
          </p>
        )}
      </GlassCard>

      {/* CHILDREN */}
      <h3 className="text-sm uppercase tracking-wider text-text-muted mt-8 mb-3 flex items-center gap-2">
        <Heart className="w-3.5 h-3.5" /> Mes enfants ({children.length})
      </h3>
      {children.length === 0 ? (
        <GlassCard className="py-8 text-center text-text-secondary text-sm">
          Aucun enfant rattaché pour l'instant. Ajoutez un email ci-dessous.
        </GlassCard>
      ) : (
        <div className="space-y-2" data-testid="parent-children-list">
          {children.map((c) => {
            const meta = STATUS_META[c.status] || STATUS_META.pending;
            const Icon = meta.icon;
            return (
              <GlassCard key={c.student_user_id} className="p-4" data-testid={`parent-child-${c.student_user_id}`}>
                <div className="flex flex-wrap gap-3 items-center justify-between">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2">
                      <span className="text-text-primary font-medium">{c.name || c.email}</span>
                      <Badge className={`${meta.cls} border text-[10px] inline-flex items-center gap-1`}>
                        <Icon className="w-3 h-3" /> {meta.fr}
                      </Badge>
                    </div>
                    <p className="text-xs text-text-muted mt-1">{c.email}</p>
                  </div>
                  <div className="flex gap-2">
                    {c.status === 'confirmed' && (
                      <Button asChild size="sm" variant="outline"
                              data-testid={`parent-child-progress-${c.student_user_id}`}>
                        <Link to={`/parent/children/${c.student_user_id}`}>
                          <Activity className="w-3.5 h-3.5 mr-1" /> Progression
                        </Link>
                      </Button>
                    )}
                    <Button size="sm" variant="ghost"
                            onClick={() => removeChild(c.student_user_id)}
                            className="text-rose-400 hover:bg-rose-500/10"
                            data-testid={`parent-child-remove-${c.student_user_id}`}>
                      <XIcon className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </GlassCard>
            );
          })}
        </div>
      )}

      {/* ADD CHILD */}
      <GlassCard className="mt-4 p-4">
        <form onSubmit={submitNewChild} className="flex gap-2 flex-wrap items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs text-text-muted mb-1 block">Ajouter un enfant</label>
            <Input type="email" required value={newEmail}
                   onChange={(e) => setNewEmail(e.target.value)}
                   placeholder="enfant@example.com"
                   className="bg-surface border-border"
                   data-testid="parent-add-child-email" />
          </div>
          <Button type="submit" disabled={adding} className="bg-primary text-background hover:bg-primary-hover"
                  data-testid="parent-add-child-btn">
            {adding ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
            Ajouter
          </Button>
        </form>
        <p className="text-xs text-text-muted mt-2">
          L'enfant doit déjà avoir un compte. Il recevra un email pour confirmer le lien.
        </p>
      </GlassCard>

      {/* COPARENTS */}
      {coparents.length > 0 && (
        <>
          <h3 className="text-sm uppercase tracking-wider text-text-muted mt-8 mb-3">Co-parents</h3>
          <div className="space-y-2" data-testid="parent-coparents-list">
            {coparents.map((cp) => (
              <GlassCard key={cp.parent_user_id} className="p-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-secondary/20 text-secondary flex items-center justify-center text-sm font-semibold">
                  {(cp.name || cp.email || '?').slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-text-primary text-sm font-medium">{cp.name || cp.email}</p>
                  <p className="text-xs text-text-muted">{cp.shared_children_count} enfant(s) en commun</p>
                </div>
              </GlassCard>
            ))}
          </div>
        </>
      )}

      {/* USAGE JOURNAL */}
      <h3 className="text-sm uppercase tracking-wider text-text-muted mt-8 mb-3 flex items-center gap-2">
        <Activity className="w-3.5 h-3.5" /> Activité récente
        <Button size="icon" variant="ghost" onClick={refresh} className="ml-auto h-6 w-6">
          <RefreshCcw className="w-3 h-3" />
        </Button>
      </h3>
      {usage.length === 0 ? (
        <GlassCard className="py-6 text-center text-text-secondary text-xs">
          Pas encore d'activité enregistrée.
        </GlassCard>
      ) : (
        <GlassCard className="p-0 overflow-hidden">
          {usage.map((u, idx) => (
            <div key={u.id || idx}
                 className={`px-4 py-3 flex items-center gap-3 ${idx > 0 ? 'border-t border-border' : ''}`}
                 data-testid={`parent-usage-row-${idx}`}>
              <div className={`w-2 h-2 rounded-full ${u.credits_consumed > 0 ? 'bg-rose-400' : u.credits_consumed < 0 ? 'bg-emerald-400' : 'bg-text-muted'}`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-text-primary truncate">{u.feature}</p>
                <p className="text-xs text-text-muted">{new Date(u.ts).toLocaleString('fr-FR')}</p>
              </div>
              <span className={`text-sm font-medium ${u.credits_consumed > 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
                {u.credits_consumed > 0 ? `−${u.credits_consumed}` : `+${-u.credits_consumed}`}
              </span>
            </div>
          ))}
        </GlassCard>
      )}
    </div>
  );
};

export default ParentDashboardPage;
